(() => {
  'use strict';

  if (window.__pickaleafFlagshipNavigationV3) return;
  window.__pickaleafFlagshipNavigationV3 = true;

  const documentElement = document.documentElement;
  const body = document.body;
  const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /*
   * ------------------------------------------------------------
   * Header compact state
   * ------------------------------------------------------------
   */

  const getHeaderBar = () => {
    return document.querySelector('.pf-header__bar');
  };

  let headerFrame = 0;

  const updateHeader = () => {
    const headerBar = getHeaderBar();
    if (!headerBar) return;

    headerBar.classList.toggle('is-compact', window.scrollY > 32);
  };

  window.addEventListener(
    'scroll',
    () => {
      cancelAnimationFrame(headerFrame);
      headerFrame = requestAnimationFrame(updateHeader);
    },
    { passive: true }
  );

  updateHeader();

  /*
   * ------------------------------------------------------------
   * Menu drawer
   *
   * The drawer must contain:
   *   data-pf-menu
   *
   * The open button must contain:
   *   data-pf-menu-open
   *
   * The close button must contain:
   *   data-pf-menu-close
   * ------------------------------------------------------------
   */

  const getMenu = () => {
    return document.querySelector('[data-pf-menu], .pf-menu');
  };

  const getMenuOpeners = () => {
    return document.querySelectorAll('[data-pf-menu-open]');
  };

  const getMenuCloser = () => {
    return document.querySelector('[data-pf-menu-close], .pf-menu__close');
  };

  let lastMenuTrigger = null;

  const setMenuState = (isOpen) => {
    const menu = getMenu();

    if (!menu) {
      console.warn(
        '[Pickaleaf] Menu drawer not found. Required selector: [data-pf-menu]'
      );
      return false;
    }

    if (isOpen) {
      menu.removeAttribute('inert');
      menu.setAttribute('aria-hidden', 'false');
      menu.classList.add('is-open');

      body.classList.add('is-menu-open');

      getMenuOpeners().forEach((button) => {
        button.setAttribute('aria-expanded', 'true');
      });

      return true;
    }

    menu.classList.remove('is-open');
    menu.setAttribute('aria-hidden', 'true');

    getMenuOpeners().forEach((button) => {
      button.setAttribute('aria-expanded', 'false');
    });

    body.classList.remove('is-menu-open');

    window.setTimeout(
      () => {
        const currentMenu = getMenu();

        if (currentMenu && !currentMenu.classList.contains('is-open')) {
          currentMenu.setAttribute('inert', '');
        }

        lastMenuTrigger?.focus({ preventScroll: true });
      },
      reducedMotion ? 0 : 650
    );

    return true;
  };

  const openMenu = (trigger) => {
    lastMenuTrigger = trigger || document.activeElement;

    const opened = setMenuState(true);
    if (!opened) return;

    window.setTimeout(
      () => {
        getMenuCloser()?.focus({ preventScroll: true });
      },
      reducedMotion ? 0 : 350
    );
  };

  const closeMenu = () => {
    setMenuState(false);
  };

  /*
   * Event delegation:
   * This also works when Shopify reloads/renders a section again.
   */

  document.addEventListener('click', (event) => {
    const openButton = event.target.closest?.('[data-pf-menu-open]');

    if (openButton) {
      event.preventDefault();
      openMenu(openButton);
      return;
    }

    const closeButton = event.target.closest?.(
      '[data-pf-menu-close], .pf-menu__close'
    );

    if (closeButton) {
      event.preventDefault();
      closeMenu();
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key === 'Escape') {
      const menu = getMenu();

      if (menu?.classList.contains('is-open')) {
        closeMenu();
      }
    }
  });

  /*
   * ------------------------------------------------------------
   * Sort select
   * ------------------------------------------------------------
   */

  document.addEventListener('change', (event) => {
    const sortSelect = event.target.closest?.('[data-pf-sort-select]');

    if (!sortSelect) return;

    sortSelect.form?.submit();
  });

  /*
   * ------------------------------------------------------------
   * Journey product links
   * ------------------------------------------------------------
   */

  const initJourneyProductLinks = (scope = document) => {
    const styleId = 'pf-journey-product-links-style';

    if (!document.getElementById(styleId)) {
      const style = document.createElement('style');

      style.id = styleId;

      style.textContent = `
        .aw-scene__visual-link,
        .bw-scene__visual-link {
          position: absolute;
          display: block;
          cursor: pointer;
          text-decoration: none;
        }

        .aw-scene__visual-link {
          z-index: 3;
          inset: 0;
        }

        .bw-scene__visual-link {
          z-index: 2;
          inset: 0;
        }

        .aw-scene__title-link,
        .bw-scene__title-link {
          color: inherit;
          text-decoration: none;
        }

        .aw-scene__title-link:hover,
        .aw-scene__title-link:focus-visible,
        .bw-scene__title-link:hover,
        .bw-scene__title-link:focus-visible {
          color: inherit;
          text-decoration: underline;
          text-decoration-color: currentColor;
          text-decoration-thickness: 1px;
          text-underline-offset: .14em;
        }

        .aw-scene__visual-link:focus-visible,
        .bw-scene__visual-link:focus-visible {
          outline: 1px solid rgba(240, 213, 139, .88);
          outline-offset: -8px;
        }

        @media (max-width: 989px) {
          .bw-scene--2 .bw-scene__visual-link,
          .bw-scene--3 .bw-scene__visual-link,
          .bw-scene--6 .bw-scene__visual-link {
            bottom: auto;
            height: calc(100% - var(--bw-caption-zone, 176px));
          }
        }
      `;

      document.head.appendChild(style);
    }

    const enhanceScene = (scene, prefix) => {
      if (!scene || scene.dataset.productLinksReady === 'true') return;

      const cta = scene.querySelector(`.${prefix}-scene__link[href]`);
      if (!cta) return;

      const href = cta.href;

      const title = scene.querySelector(
        `.${prefix}-scene__title-row h2`
      );

      const label = title?.textContent.trim() || 'View product';

      cta.dataset.noTransition = '';

      if (title && !title.querySelector('a')) {
        const titleLink = document.createElement('a');

        titleLink.className = `${prefix}-scene__title-link`;
        titleLink.href = href;
        titleLink.dataset.noTransition = '';
        titleLink.setAttribute('aria-label', `View ${label}`);

        while (title.firstChild) {
          titleLink.appendChild(title.firstChild);
        }

        title.appendChild(titleLink);
      }

      if (!scene.querySelector(`.${prefix}-scene__visual-link`)) {
        const visualLink = document.createElement('a');

        visualLink.className = `${prefix}-scene__visual-link`;
        visualLink.href = href;
        visualLink.dataset.noTransition = '';
        visualLink.setAttribute('aria-label', `View ${label}`);

        const veil = scene.querySelector(
          `.${prefix}-scene__veil`
        );

        if (veil) {
          scene.insertBefore(visualLink, veil.nextSibling);
        } else {
          scene.prepend(visualLink);
        }
      }

      scene.dataset.productLinksReady = 'true';
    };

    scope.querySelectorAll('.aw-scene').forEach((scene) => {
      enhanceScene(scene, 'aw');
    });

    scope.querySelectorAll('.bw-scene').forEach((scene) => {
      enhanceScene(scene, 'bw');
    });

    scope
      .querySelectorAll(
        '.pickaleaf-catalog__card[href*="/collections/blossom-wood"]'
      )
      .forEach((card) => {
        card.dataset.instantWorld = '';
        card.dataset.noTransition = '';
      });
  };

  /*
   * ------------------------------------------------------------
   * Shopify section lifecycle
   * ------------------------------------------------------------
   */

  document.addEventListener('shopify:section:load', (event) => {
    initJourneyProductLinks(event.target || document);
    updateHeader();
  });

  initJourneyProductLinks();

  documentElement.classList.add('pf-ready');
})();